import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from 'lucide-react';
import { apiClient } from '@/lib/api';

type CoherenceSeverity = 'error' | 'warning' | 'info';

interface CoherenceWarning {
  code: string;
  severity: CoherenceSeverity;
  title: string;
  detail: string;
  affected?: string[];
}

interface FormCoherenceReport {
  form_id: string;
  consultant_filter_mode: 'any' | 'skill_shifts' | 'manual';
  consultant_skill_filter: boolean;
  calendar_mode: {
    effective: 'LEGGERA' | 'PIANIFICATA' | 'RIGOROSA';
    configured: string;
    auto: boolean;
  };
  warnings: CoherenceWarning[];
}

interface FormCoherencePanelProps {
  /** Form persistito da valutare. `null` finché il form non è salvato. */
  formId: string | null;
  /** Incrementa per forzare il refetch (es. dopo un salvataggio). */
  refreshToken?: number;
}

const SEVERITY_STYLE: Record<
  CoherenceSeverity,
  { box: string; icon: typeof Info; iconClass: string }
> = {
  error: {
    box: 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40',
    icon: XCircle,
    iconClass: 'text-red-600 dark:text-red-400',
  },
  warning: {
    box: 'border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40',
    icon: AlertTriangle,
    iconClass: 'text-amber-600 dark:text-amber-400',
  },
  info: {
    box: 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40',
    icon: Info,
    iconClass: 'text-blue-600 dark:text-blue-400',
  },
};

const MODE_LABEL: Record<FormCoherenceReport['calendar_mode']['effective'], string> = {
  LEGGERA: 'Leggera',
  PIANIFICATA: 'Pianificata',
  RIGOROSA: 'Rigorosa',
};

export default function FormCoherencePanel({ formId, refreshToken = 0 }: FormCoherencePanelProps) {
  const [report, setReport] = useState<FormCoherenceReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!formId) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<FormCoherenceReport>(`/calendar/forms/${formId}/coherence`);
      if (res.success && res.data) {
        setReport(res.data);
      } else {
        setError(res.error || 'Impossibile calcolare la coerenza');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile calcolare la coerenza');
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  if (!formId) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-3 text-xs text-gray-500 dark:text-gray-400">
        Salva il form per vedere il controllo di coerenza tra modalità consulente e regole di
        disponibilità del calendario.
      </div>
    );
  }

  const blocking = report?.warnings.filter((w) => w.severity !== 'info') ?? [];

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Coerenza con le regole di disponibilità</span>
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
        </div>
        {report && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Calendario:{' '}
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {MODE_LABEL[report.calendar_mode.effective]}
            </span>
            {report.calendar_mode.auto && <span className="ml-1 italic">(auto)</span>}
          </span>
        )}
      </div>

      {error && (
        <div className="text-xs text-red-600 dark:text-red-400">{error}</div>
      )}

      {report && !loading && blocking.length === 0 && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-4 w-4" />
          Nessuna contraddizione rilevata tra la modalità del form e le regole di disponibilità.
        </div>
      )}

      {report && (
        <ul className="space-y-2">
          {report.warnings.map((w) => {
            const style = SEVERITY_STYLE[w.severity];
            const Icon = style.icon;
            return (
              <li key={w.code} className={`flex gap-2 rounded-md border p-2 ${style.box}`}>
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${style.iconClass}`} />
                <div className="min-w-0 space-y-1">
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-100">{w.title}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">{w.detail}</p>
                  {w.affected && w.affected.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-0.5">
                      {w.affected.map((a) => (
                        <span
                          key={a}
                          className="inline-block rounded bg-white/70 dark:bg-black/30 border border-gray-200 dark:border-gray-700 px-1.5 py-0.5 text-[11px] text-gray-700 dark:text-gray-200"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-[11px] text-gray-400 dark:text-gray-500">
        Controllo di sola lettura: non modifica il comportamento del form. Gli avvisi si aggiornano
        al salvataggio.
      </p>
    </div>
  );
}
